import { RenderManager } from '../../../core/RenderManager';
import { AudioManager } from '../../../core/AudioManager';
import { Lander } from '../entities/Lander';
import { Humanoid } from '../entities/Humanoid';
import { DefenderShip } from '../entities/DefenderShip';

export class EnemyManager {
  private worldWidth: number;
  private renderManager: RenderManager;
  private audioManager: AudioManager;
  private enemies: any[] = [];
  private spawnTimer: number = 0;
  private spawnInterval: number = 3; // Spawn enemy every 3 seconds
  private maxEnemies: number = 8;
  private waveEnemiesRemaining: number = 0;
  private baiterTimer: number = 0;
  private baiterDelay: number = 30; // Spawn baiter if player takes too long (30 seconds)
  private waveStarted: boolean = false; // Track if wave has actually started spawning

  constructor(worldWidth: number, renderManager: RenderManager, audioManager: AudioManager) {
    this.worldWidth = worldWidth;
    this.renderManager = renderManager;
    this.audioManager = audioManager;
  }

  public initializeWave(waveNumber: number): void {
    this.enemies = [];
    this.spawnTimer = 0;
    this.baiterTimer = 0;
    this.waveStarted = false;
    
    // Calculate enemies for this wave
    this.waveEnemiesRemaining = Math.min(20, 8 + waveNumber * 2); // Start with 10, increase by 2 per wave, max 20
    this.maxEnemies = Math.min(12, 8 + Math.floor(waveNumber / 2)); // Increase max on screen
    this.spawnInterval = Math.max(1, 3 - waveNumber * 0.1); // Faster spawning on later waves
    
    console.log(`Wave ${waveNumber}: ${this.waveEnemiesRemaining} enemies, max ${this.maxEnemies} on screen`);
  }

  public update(deltaTime: number, humanoids: Humanoid[], ship: DefenderShip | null): void {
    // Update spawn timer
    this.spawnTimer += deltaTime;
    this.baiterTimer += deltaTime;
    
    // Update existing enemies
    this.enemies = this.enemies.filter(enemy => {
      if (!enemy.active) return false;
      
      if (enemy.type === 'lander') {
        enemy.setHumanoids(humanoids); // Set humanoids reference
      }
      enemy.update(deltaTime);
      
      return enemy.active;
    });
    
    // Spawn new enemies if needed
    if (this.shouldSpawnEnemy()) {
      this.spawnEnemy();
    }
    
    // Spawn baiter if player is taking too long
    if (this.shouldSpawnBaiter()) {
      this.spawnBaiter();
      this.baiterTimer = 0;
    }
  }

  private shouldSpawnEnemy(): boolean {
    const should = this.spawnTimer >= this.spawnInterval &&
           this.enemies.length < this.maxEnemies &&
           this.waveEnemiesRemaining > 0;
           
    if (this.spawnTimer >= this.spawnInterval && this.waveEnemiesRemaining > 0) {
      console.log(`Spawn check: timer=${this.spawnTimer.toFixed(2)}, enemies=${this.enemies.length}/${this.maxEnemies}, remaining=${this.waveEnemiesRemaining}, should=${should}`);
    }
    
    return should;
  }

  private shouldSpawnBaiter(): boolean {
    return this.baiterTimer >= this.baiterDelay &&
           this.enemies.filter(e => e.type === 'baiter').length === 0; // Only one baiter at a time
  }

  private spawnEnemy(): void {
    // For now, only spawn landers
    // Later we can add variety based on wave number
    
    const spawnX = Math.random() * this.worldWidth;
    const spawnY = 50; // Spawn at top of screen
    
    const lander = new Lander(
      `lander_${Date.now()}`,
      spawnX,
      spawnY,
      this.renderManager,
      this.audioManager,
      this.worldWidth
    );
    
    this.enemies.push(lander);
    this.waveEnemiesRemaining--;
    this.spawnTimer = 0;
    this.waveStarted = true; // Mark that wave has started spawning
    
    console.log(`Spawned lander at (${spawnX}, ${spawnY}). ${this.waveEnemiesRemaining} enemies remaining.`);
  }

  private spawnBaiter(): void {
    // Baiter spawns near player and chases aggressively
    // For now, create a simple chasing enemy
    
    // TODO: Implement Baiter enemy class
    console.log('Baiter spawn triggered (not yet implemented)');
  }

  public removeEnemy(enemyId: string): boolean {
    const index = this.enemies.findIndex(enemy => enemy.id === enemyId);
    if (index !== -1) {
      this.enemies.splice(index, 1);
      return true;
    }
    return false;
  }

  public getEnemies(): any[] {
    return [...this.enemies];
  }

  public getActiveEnemyCount(): number {
    return this.enemies.filter(enemy => enemy.active).length;
  }

  public isWaveComplete(): boolean {
    // Wave is only complete if it has started AND all enemies are spawned and destroyed
    return this.waveStarted && this.waveEnemiesRemaining === 0 && this.getActiveEnemyCount() === 0;
  }

  public getEnemiesNear(x: number, y: number, radius: number): any[] {
    return this.enemies.filter(enemy => {
      if (!enemy.active) return false;
      
      const dx = enemy.position.x - x;
      const dy = enemy.position.y - y;
      const distance = Math.sqrt(dx * dx + dy * dy);
      
      return distance <= radius;
    });
  }

  public getLanders(): Lander[] {
    return this.enemies.filter(enemy => enemy.type === 'lander') as Lander[];
  }

  public getAbductingLanders(): Lander[] {
    return this.getLanders().filter(lander => lander.isAbducting());
  }
}